import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { usePreferences } from '../../contexts/PreferencesContext';
import { useData } from '../../contexts/DataContext';
import { UsageEventsTracker } from '../../services/UsageEventsTracker';
import { UserProfileApi } from '../../services/api/UserProfileApi';
import { shouldShowOnboarding } from '../../utils/onboardingEligibility';
import WelcomeStep from './steps/WelcomeStep';
import ProfileStep from './steps/ProfileStep';
import SyllabusUploadStep from './steps/SyllabusUploadStep';
import ScanWaitStep from './steps/ScanWaitStep';
import CourseReviewStep from './steps/CourseReviewStep';
import DocsUploadStep from './steps/DocsUploadStep';
import AIToolsStep from './steps/AIToolsStep';
import CompleteStep from './steps/CompleteStep';
import './OnboardingWizard.css';

/**
 * Build the dynamic step list.
 * Fixed steps: welcome → profile → syllabus-upload → scan-wait
 * Per-course loop: [course-review-N, docs-upload-N] for each syllabus
 * Then: ai-tools → complete
 */
function buildSteps(wizardData) {
  const steps = [
    { id: 'welcome',         label: 'Welcome',         skippable: false, width: 'normal' },
    { id: 'profile',         label: 'About You',       skippable: false, width: 'normal' },
    { id: 'syllabus-upload', label: 'Upload Syllabus',  skippable: true,  width: 'normal' },
    { id: 'scan-wait',       label: 'Processing',       skippable: true,  width: 'extra-wide' },
  ];

  // Course count = syllabi uploaded + manual entries (hybrid mode supported).
  // In pure manual mode (no syllabi), just use manualCourseEntries.
  // In hybrid mode, syllabi courses come first, then manual courses.
  const numSyllabi = wizardData.scanIds?.length || 0;
  const numManual = wizardData.manualCourseEntries?.length || 0;
  const courseCount = numSyllabi > 0 && numManual > 0
    ? numSyllabi + numManual          // hybrid: syllabi + manual
    : Math.max(numSyllabi, numManual, 1); // pure upload or pure manual (at least 1)

  for (let i = 0; i < courseCount; i++) {
    steps.push({
      id: `course-review-${i}`,
      label: courseCount > 1 ? `Review Course ${i + 1}` : 'Review Course',
      skippable: false,
      width: 'wide',
      courseIndex: i,
      stepType: 'course-review',
    });
    steps.push({
      id: `docs-upload-${i}`,
      label: courseCount > 1 ? `Upload Docs ${i + 1}` : 'Upload Docs',
      skippable: true,
      width: 'normal',
      courseIndex: i,
      stepType: 'docs-upload',
    });
  }

  steps.push({ id: 'ai-tools', label: 'AI Tools', skippable: true, width: 'extra-wide' });
  steps.push({ id: 'complete', label: 'Done!', skippable: false, width: 'normal' });

  return steps;
}

/**
 * Look up the scan result for a specific course index.
 * Uses scanIds for positional mapping — scanIds[i] corresponds to course i.
 * The primary scan result (index 0) is stored in wizardData.scanResult.
 * Background results are in wizardData.scanResults[] with _scanId tags.
 */
// eslint-disable-next-line no-unused-vars
function getScanResultForCourseIndex(wizardData, courseIndex) {
  // Course 0 — prefer the eagerly-set primary scanResult
  if (courseIndex === 0 && wizardData.scanResult) return wizardData.scanResult;

  const scanIds = wizardData.scanIds || [];
  const targetScanId = scanIds[courseIndex];
  if (!targetScanId) return null;

  // Search background results by matching _scanId
  if (Array.isArray(wizardData.scanResults)) {
    const match = wizardData.scanResults.find(
      sr => sr?._scanId != null && String(sr._scanId) === String(targetScanId)
    );
    if (match) return match;
  }

  return null;
}

// Step transition variants – used by AnimatePresence in renderStep
// eslint-disable-next-line no-unused-vars
const _stepAnimation = {
  initial: { opacity: 0, x: 40 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -40 },
  transition: { duration: 0.3, ease: 'easeInOut' },
};

export default function OnboardingWizard() {
  const { user, hasAnyOrgAdminRole, getAdminOrgIds } = useAuth();
  const { preferences: uiPreferences, patchPreferences, flushPreferences } = usePreferences();
  const { courses, clearCache, isDashboardHydrated, hydrateDashboard } = useData();
  const navigate = useNavigate();

  // Kick off dashboard hydration so we know if the user already has courses
  const userId = user?.id || user?.userId;
  useEffect(() => {
    if (userId && hydrateDashboard) hydrateDashboard(userId);
  }, [userId, hydrateDashboard]);

  // Guard: redirect existing / already-completed users away from onboarding
  useEffect(() => {
    if (!user) return;
    if (uiPreferences === undefined || uiPreferences === null) return;
    // Wait for dashboard data so we know whether the user has existing courses.
    // isDashboardHydrated is a getter function; call it to read the ref.
    if (typeof isDashboardHydrated === 'function' ? !isDashboardHydrated() : !isDashboardHydrated) return;
    if (!shouldShowOnboarding({ user, uiPreferences: uiPreferences || {}, hasCourses: courses && courses.length > 0 })) {
      navigate('/dashboard', { replace: true });
    }
  // courses changes when hydration populates coursesById, which re-triggers this effect
  }, [user, uiPreferences, isDashboardHydrated, courses, navigate]);

  // --- Wizard state ---
  const [currentStepIdx, setCurrentStepIdx] = useState(0);
  const [direction, setDirection] = useState(1); // 1 = forward, -1 = back

  // --- Step timing refs for analytics ---
  const stepViewedAtRef = useRef(Date.now());
  const onboardingStartRef = useRef(Date.now());

  // Shared data passed between steps
  const [wizardData, setWizardData] = useState({
    // Profile
    school: '',
    year: '',
    major: '',
    courseCount: 4,
    // Syllabus (primary — first uploaded)
    syllabusDocumentId: null,
    syllabusFile: null,
    scanId: null,
    scanResult: null,
    // Multi-syllabus support
    syllabusDocumentIds: [],   // all uploaded syllabus doc IDs
    syllabusFiles: [],         // { name, size, type } for each
    scanIds: [],               // scan ID per syllabus
    scanResults: [],           // completed scan results (background)
    // Multi-course loop
    courses: [],               // [{ courseId, courseName, sessionCount, assignmentCount, uploadedDocIds, syllabusDocumentId }]
    // AI tools demo
    aiToolsCourseId: null,     // which course the user selected for demos
    flashcardSetId: null,
    flashcards: [],
    quizSetId: null,
    quizQuestions: [],
    // Legacy compat (set to first/active course)
    courseId: null,
    courseName: '',
    sessionCount: 0,
    assignmentCount: 0,
    // Docs uploaded during onboarding (global — for backward compat / hasDocuments check)
    uploadedDocIds: [],
    // Manual mode (skip syllabus)
    manualMode: false,
    manualCourseData: null,
    manualCourseEntries: [],
  });

  // Dynamic steps based on how many syllabi were uploaded
  const STEPS = useMemo(() => buildSteps(wizardData), [wizardData]);

  const currentStep = STEPS[currentStepIdx] || STEPS[0];

  // Does the user have any uploaded documents?
  const hasDocuments = useMemo(() => {
    const hasGlobalDocs = (wizardData.syllabusDocumentIds && wizardData.syllabusDocumentIds.length > 0) ||
      !!wizardData.syllabusDocumentId ||
      (wizardData.uploadedDocIds && wizardData.uploadedDocIds.length > 0);
    const hasCourseDocs = wizardData.courses.some(c => c?.uploadedDocIds?.length > 0);
    return hasGlobalDocs || hasCourseDocs;
  }, [wizardData.syllabusDocumentIds, wizardData.syllabusDocumentId, wizardData.uploadedDocIds, wizardData.courses]);

  // Number of courses in the loop — must match buildSteps logic
  const totalCourses = useMemo(() => {
    const numSyllabi = wizardData.scanIds?.length || 0;
    const numManual = wizardData.manualCourseEntries?.length || 0;
    return numSyllabi > 0 && numManual > 0
      ? numSyllabi + numManual
      : Math.max(numSyllabi, numManual, 1);
  }, [wizardData.scanIds, wizardData.manualCourseEntries]);

  // Steps visible in progress bar — collapse per-course loop into summary segments
  const progressSteps = useMemo(() => {
    const result = [];
    let courseGroupAdded = false;
    for (const s of STEPS) {
      if (s.stepType === 'course-review' || s.stepType === 'docs-upload') {
        if (!courseGroupAdded) {
          result.push({ id: '_courses', label: totalCourses > 1 ? `Courses (${totalCourses})` : 'Course Setup' });
          courseGroupAdded = true;
        }
        // skip individual per-course steps in progress bar
      } else if (s.id === 'ai-tools') {
        if (!hasDocuments) continue; // hide AI tools from progress when no docs
        result.push(s);
      } else {
        result.push(s);
      }
    }
    return result;
  }, [STEPS, totalCourses, hasDocuments]);

  // --- Persistence: resume from last step ---
  useEffect(() => {
    try {
      const saved = localStorage.getItem(`dh_onboarding_progress:${user?.userId || user?.id}`);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Restore wizard data first so STEPS can rebuild
        if (parsed.wizardData) {
          const w = parsed.wizardData;
          const safe = {
            ...w,
            school: typeof w.school === 'string' ? w.school : '',
            year: typeof w.year === 'string' ? w.year : '',
            major: typeof w.major === 'string' ? w.major : '',
            courseCount: typeof w.courseCount === 'number' ? w.courseCount : 3,
            courseName: typeof w.courseName === 'string' ? w.courseName : '',
            syllabusDocumentIds: Array.isArray(w.syllabusDocumentIds)
              ? w.syllabusDocumentIds.filter(id => typeof id === 'string' || typeof id === 'number')
              : [],
            syllabusFiles: Array.isArray(w.syllabusFiles) ? w.syllabusFiles : [],
            scanIds: Array.isArray(w.scanIds)
              ? w.scanIds.filter(id => typeof id === 'string' || typeof id === 'number')
              : [],
            scanResults: Array.isArray(w.scanResults) ? w.scanResults : [],
            courses: Array.isArray(w.courses) ? w.courses : [],
            manualCourseEntries: Array.isArray(w.manualCourseEntries) ? w.manualCourseEntries : [],
            uploadedDocIds: Array.isArray(w.uploadedDocIds)
              ? w.uploadedDocIds.filter(id => typeof id === 'string' || typeof id === 'number')
              : [],
            flashcards: Array.isArray(w.flashcards) ? w.flashcards : [],
            quizQuestions: Array.isArray(w.quizQuestions) ? w.quizQuestions : [],
          };
          setWizardData(prev => ({ ...prev, ...safe }));
        }
        // Restore step index — clamp to valid range for the rebuilt STEPS
        if (parsed.stepIdx != null) {
          // We'll set it after state is updated in a separate effect
          setCurrentStepIdx(prev => {
            const maxIdx = parsed.wizardData
              ? buildSteps(parsed.wizardData).length - 1
              : prev;
            return Math.min(parsed.stepIdx, maxIdx);
          });

          // Track session resumption
          const resumedSteps = parsed.wizardData ? buildSteps(parsed.wizardData) : [];
          const resumedStep = resumedSteps[Math.min(parsed.stepIdx, resumedSteps.length - 1)];
          UsageEventsTracker.recordUserAction('ONBOARDING_INTERACTION', {
            action: 'session_resumed',
            category: 'onboarding',
            metadata: {
              resumedAtStep: resumedStep?.id || 'unknown',
              resumedAtStepIndex: parsed.stepIdx,
            },
          });
        }
      }
    } catch (e) { /* ignore */ }
  }, [user]);

  // Save progress on step change
  useEffect(() => {
    try {
      const userId = user?.userId || user?.id;
      if (userId) {
        localStorage.setItem(`dh_onboarding_progress:${userId}`, JSON.stringify({
          stepIdx: currentStepIdx,
          wizardData,
        }));
      }
    } catch (e) { /* ignore */ }
  }, [currentStepIdx, wizardData, user]);

  // --- Navigation ---
  const goNext = useCallback(() => {
    // Persist profile data to backend when leaving the profile step
    if (currentStep?.id === 'profile') {
      const userId = user?.userId || user?.id;
      if (userId && wizardData.school) {
        UserProfileApi.patch(userId, {
          school: wizardData.school,
          academicYear: wizardData.year,
          major: wizardData.major,
          courseCount: wizardData.courseCount,
        }).catch(err => console.warn('[OnboardingWizard] Failed to save user profile:', err));

        patchPreferences({
          onboarding_profile: {
            school: wizardData.school,
            year: wizardData.year,
            major: wizardData.major,
            courseCount: wizardData.courseCount,
          },
        });
      }
    }
    setDirection(1);
    setCurrentStepIdx(prev => {
      let next = Math.min(prev + 1, STEPS.length - 1);
      // Skip AI tools step when user has no uploaded documents
      if (STEPS[next]?.id === 'ai-tools' && !hasDocuments) {
        next = Math.min(next + 1, STEPS.length - 1);
      }
      // Skip scan-wait, course-review, and docs-upload when user joined a shared course
      if (wizardData.joinedSharedCourse) {
        while (next < STEPS.length - 1 && (
          STEPS[next]?.id === 'scan-wait' ||
          STEPS[next]?.stepType === 'course-review' ||
          STEPS[next]?.stepType === 'docs-upload' ||
          (STEPS[next]?.id === 'ai-tools' && !hasDocuments)
        )) {
          next = Math.min(next + 1, STEPS.length - 1);
        }
      }
      return next;
    });
    // Track
    UsageEventsTracker.recordUserAction('ONBOARDING_STEP_COMPLETED', {
      action: 'navigate',
      category: 'onboarding',
      label: currentStep?.id,
      metadata: {
        stepIndex: currentStepIdx,
        totalSteps: STEPS.length,
        mode: wizardData.manualMode ? 'manual' : 'syllabus',
      },
    });
  }, [currentStep, currentStepIdx, user, wizardData, patchPreferences, hasDocuments, STEPS]);

  const goBack = useCallback(() => {
    UsageEventsTracker.recordUserAction('ONBOARDING_INTERACTION', {
      action: 'back_button_click',
      category: 'onboarding',
      label: currentStep?.id,
      metadata: {
        fromStep: currentStep?.id,
        fromStepIndex: currentStepIdx,
      },
    });
    setDirection(-1);
    setCurrentStepIdx(prev => {
      let next = Math.max(prev - 1, 0);
      // Skip AI tools step when going back if user has no documents
      if (STEPS[next]?.id === 'ai-tools' && !hasDocuments) {
        next = Math.max(next - 1, 0);
      }
      return next;
    });
  }, [hasDocuments, STEPS, currentStep, currentStepIdx]);

  const goToStep = useCallback((stepId) => {
    const idx = STEPS.findIndex(s => s.id === stepId);
    if (idx >= 0) {
      setDirection(idx > currentStepIdx ? 1 : -1);
      setCurrentStepIdx(idx);
    }
  }, [currentStepIdx, STEPS]);

  const skipStep = useCallback(() => {
    UsageEventsTracker.recordUserAction('ONBOARDING_STEP_SKIPPED', {
      action: 'skip',
      category: 'onboarding',
      label: currentStep?.id,
      metadata: {
        stepIndex: currentStepIdx,
        totalSteps: STEPS.length,
        mode: wizardData.manualMode ? 'manual' : 'syllabus',
      },
    });
    goNext();
  }, [currentStep, currentStepIdx, goNext, STEPS.length, wizardData.manualMode]);

  // Supports both object and function arguments (like setState).
  // Function form: updateData(prev => ({ key: newValue })) — avoids stale closures.
  const updateData = useCallback((updaterOrUpdates) => {
    if (typeof updaterOrUpdates === 'function') {
      setWizardData(prev => ({ ...prev, ...updaterOrUpdates(prev) }));
    } else {
      setWizardData(prev => ({ ...prev, ...updaterOrUpdates }));
    }
  }, []);

  // --- Complete onboarding ---
  const completeOnboarding = useCallback(async () => {
    const userId = user?.userId || user?.id;
    try {
      patchPreferences({ onboarding_v2_completed: true, onboarding_mode: 'system' });
      await flushPreferences();
    } catch (e) {
      console.warn('[OnboardingWizard] Failed to persist completion:', e);
    }
    localStorage.setItem(`dh_onboarding_complete:${userId}`, '1');
    localStorage.removeItem(`dh_onboarding_progress:${userId}`);
    UsageEventsTracker.recordUserAction('ONBOARDING_COMPLETED', {
      action: 'complete',
      category: 'onboarding',
      metadata: {
        stepsCompleted: STEPS.length,
        coursesCreated: wizardData.courses.length,
        mode: wizardData.manualMode ? 'manual' : 'syllabus',
        docsUploaded: (wizardData.uploadedDocIds?.length || 0),
        totalDurationMs: Date.now() - onboardingStartRef.current,
      },
    });
    // Reset dashboard hydration so Dashboard.js re-fetches all data with correct userId
    if (clearCache) clearCache('dashboard_full_hydration');
    navigate('/dashboard', { replace: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, navigate, patchPreferences, flushPreferences, STEPS.length, wizardData.courses.length, clearCache]);

  // --- Skip all (go to dashboard) ---
  const skipAll = useCallback(async () => {
    UsageEventsTracker.recordUserAction('ONBOARDING_ABANDONED', {
      action: 'skip_all',
      category: 'onboarding',
      label: currentStep?.id,
      metadata: {
        stepIndex: currentStepIdx,
        totalSteps: STEPS.length,
        mode: wizardData.manualMode ? 'manual' : 'syllabus',
        totalDurationMs: Date.now() - onboardingStartRef.current,
      },
    });
    const userId = user?.userId || user?.id;
    try {
      patchPreferences({ onboarding_v2_completed: true, onboarding_mode: 'system' });
      await flushPreferences();
    } catch (e) { /* fire-and-forget */ }
    localStorage.setItem(`dh_onboarding_complete:${userId}`, '1');
    localStorage.removeItem(`dh_onboarding_progress:${userId}`);
    // Reset dashboard hydration so Dashboard.js re-fetches all data with correct userId
    if (clearCache) clearCache('dashboard_full_hydration');
    navigate('/dashboard', { replace: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, currentStepIdx, user, navigate, patchPreferences, flushPreferences, clearCache]);

  // --- Skip to tools (from welcome intent selection) ---
  const skipToTools = useCallback(async () => {
    const userId = user?.userId || user?.id;
    UsageEventsTracker.recordUserAction('ONBOARDING_INTERACTION', {
      action: 'skip_to_tools',
      category: 'onboarding',
      label: 'welcome_intent_use_tools',
      metadata: {
        totalDurationMs: Date.now() - onboardingStartRef.current,
      },
    });
    try {
      patchPreferences({ onboarding_v2_completed: true, onboarding_mode: 'tools' });
      await flushPreferences();
    } catch (e) { /* fire-and-forget */ }
    localStorage.setItem(`dh_onboarding_complete:${userId}`, '1');
    localStorage.removeItem(`dh_onboarding_progress:${userId}`);
    if (clearCache) clearCache('dashboard_full_hydration');
    navigate('/ai-tools', { replace: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, navigate, patchPreferences, flushPreferences, clearCache]);

  // --- Skip to organization admin (from welcome intent selection) ---
  const skipToOrg = useCallback(async () => {
    const userId = user?.userId || user?.id;
    UsageEventsTracker.recordUserAction('ONBOARDING_INTERACTION', {
      action: 'skip_to_org',
      category: 'onboarding',
      label: 'welcome_intent_setup_organization',
      metadata: {
        totalDurationMs: Date.now() - onboardingStartRef.current,
      },
    });
    try {
      patchPreferences({ onboarding_v2_completed: true, onboarding_mode: 'org_admin' });
      await flushPreferences();
    } catch (e) { /* fire-and-forget */ }
    localStorage.setItem(`dh_onboarding_complete:${userId}`, '1');
    localStorage.removeItem(`dh_onboarding_progress:${userId}`);
    if (clearCache) clearCache('dashboard_full_hydration');

    // Navigate to the org admin dashboard — if exactly one org, go directly;
    // otherwise go to the multi-org picker page.
    const adminOrgIds = getAdminOrgIds ? getAdminOrgIds() : [];
    if (adminOrgIds.length === 1) {
      navigate(`/organization/${adminOrgIds[0]}/admin`, { replace: true });
    } else {
      navigate('/admin/organizations', { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, navigate, patchPreferences, flushPreferences, clearCache, getAdminOrgIds]);

  // Track onboarding start
  useEffect(() => {
    onboardingStartRef.current = Date.now();
    UsageEventsTracker.recordUserAction('ONBOARDING_STARTED', {
      action: 'view',
      category: 'onboarding',
      metadata: {
        mode: wizardData.manualMode ? 'manual' : 'syllabus',
        courseCount: wizardData.courseCount,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Track step views and emit duration for the departing step
  useEffect(() => {
    const step = STEPS[currentStepIdx];
    if (!step) return;
    const now = Date.now();
    const elapsed = now - stepViewedAtRef.current;
    // Emit duration for the previous step (skip on first render when elapsed ~0)
    if (elapsed > 100) {
      UsageEventsTracker.recordUserAction('ONBOARDING_STEP_DURATION', {
        action: 'duration',
        category: 'onboarding',
        label: STEPS[currentStepIdx - 1]?.id || 'unknown',
        value: elapsed,
        metadata: { stepIndex: currentStepIdx - 1 },
      });
    }
    stepViewedAtRef.current = now;
    // Emit step viewed
    UsageEventsTracker.recordUserAction('ONBOARDING_STEP_VIEWED', {
      action: 'view',
      category: 'onboarding',
      entityType: 'step',
      entityId: step.id,
      label: step.label,
      metadata: {
        stepIndex: currentStepIdx,
        totalSteps: STEPS.length,
        mode: wizardData.manualMode ? 'manual' : 'syllabus',
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStepIdx]);

  // --- Width class ---
  const widthClass = useMemo(() => {
    if (currentStep?.width === 'wide') return 'wide';
    if (currentStep?.width === 'extra-wide') return 'extra-wide';
    return '';
  }, [currentStep]);

  // --- Determine which progress segment is active ---
  const progressInfo = useMemo(() => {
    // Map current step to a progress segment
    let activeProgressIdx = 0;
    if (currentStep?.stepType === 'course-review' || currentStep?.stepType === 'docs-upload') {
      // We're in the per-course loop — find the _courses segment
      activeProgressIdx = progressSteps.findIndex(s => s.id === '_courses');
    } else {
      activeProgressIdx = progressSteps.findIndex(s => s.id === currentStep?.id);
    }
    // Count completed progress segments
    const currentStepOrder = STEPS.findIndex(s => s.id === currentStep?.id);
    return { activeProgressIdx: Math.max(activeProgressIdx, 0), currentStepOrder };
  }, [currentStep, STEPS, progressSteps]);

  // --- Common step props ---
  const stepProps = {
    data: wizardData,
    updateData,
    goNext,
    goBack,
    goToStep,
    skipStep,
    user,
  };

  const renderStep = () => {
    if (!currentStep) return null;

    if (currentStep.stepType === 'course-review') {
      return (
        <CourseReviewStep
          {...stepProps}
          courseIndex={currentStep.courseIndex}
          totalCourses={totalCourses}
        />
      );
    }
    if (currentStep.stepType === 'docs-upload') {
      return (
        <DocsUploadStep
          {...stepProps}
          courseIndex={currentStep.courseIndex}
          totalCourses={totalCourses}
        />
      );
    }

    switch (currentStep.id) {
      case 'welcome':         return <WelcomeStep {...stepProps} onSkipToTools={skipToTools} onSkipToOrg={skipToOrg} isOrgAdmin={hasAnyOrgAdminRole && hasAnyOrgAdminRole()} />;
      case 'profile':         return <ProfileStep {...stepProps} />;
      case 'syllabus-upload': return <SyllabusUploadStep {...stepProps} />;
      case 'scan-wait':       return <ScanWaitStep {...stepProps} />;
      case 'ai-tools':        return <AIToolsStep {...stepProps} />;
      case 'complete':        return <CompleteStep {...stepProps} onComplete={completeOnboarding} />;
      default:                return null;
    }
  };

  // Compute per-course sub-progress for the courses segment
  const courseSubProgress = useMemo(() => {
    if (totalCourses <= 1) return null;
    if (currentStep?.stepType !== 'course-review' && currentStep?.stepType !== 'docs-upload') return null;
    const idx = currentStep.courseIndex;
    const isReview = currentStep.stepType === 'course-review';
    return { current: idx + 1, total: totalCourses, phase: isReview ? 'Review' : 'Documents' };
  }, [currentStep, totalCourses]);

  return (
    <div className="onboarding-wizard">
      {/* Header */}
      <header className="onboarding-header">
        <div className="onboarding-header-left">
          <img src="/scout_images/scout_wave_new.png" alt="Scout" className="onboarding-logo" />
          <span className="onboarding-header-title">Welcome to DocuHounds</span>
        </div>
        <div className="onboarding-header-right">
          {currentStepIdx > 0 && currentStep?.id !== 'complete' && (
            <button className="onboarding-skip-link" onClick={skipAll}>
              Skip setup →
            </button>
          )}
        </div>
      </header>

      {/* Progress bar */}
      <div className="onboarding-progress">
        <div className="onboarding-progress-bar-container">
          <div className="onboarding-progress-steps">
            {progressSteps.map((step, idx) => {
              // Determine if this segment is completed, active, or upcoming
              let state = '';
              if (idx < progressInfo.activeProgressIdx) state = 'completed';
              else if (idx === progressInfo.activeProgressIdx) state = 'active';
              return (
                <div
                  key={step.id}
                  className={`onboarding-progress-step ${state}`}
                />
              );
            })}
          </div>
          <span className="onboarding-progress-label">
            Step {progressInfo.activeProgressIdx + 1} of {progressSteps.length}
            {courseSubProgress && (
              <> — Course {courseSubProgress.current} of {courseSubProgress.total}: {courseSubProgress.phase}</>
            )}
          </span>
        </div>
      </div>

      {/* Step content */}
      <main className="onboarding-content">
        <div className={`onboarding-step-container ${widthClass}`}>
          <AnimatePresence mode="wait" custom={direction}>
            <motion.div
              key={currentStep?.id || 'unknown'}
              custom={direction}
              initial={{ opacity: 0, x: direction * 40 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: direction * -40 }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
            >
              {renderStep()}
            </motion.div>
          </AnimatePresence>
        </div>
      </main>
    </div>
  );
}
